import { useState, useMemo, useCallback } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs'
import { tokensApi } from '../../../../lib/api/tokens'
import { Button } from '../../../../components/ui/button'
import { Card, CardContent } from '../../../../components/ui/card'
import { Badge } from '../../../../components/ui/badge'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../../../components/ui/alert-dialog'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../../../components/ui/dialog'
import { AppIcon } from '../../../../components/ui/icon'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '../../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../../lib/section-icons'
import { EmptyState } from '../../../../components/ui/empty-state'
import { mdiCalendar, mdiCheck, mdiClockOutline, mdiContentCopy, mdiKey, mdiKeyVariant, mdiOpenInNew, mdiPlus, mdiTrashCanOutline } from '@mdi/js'

export const Route = createFileRoute('/$org/user/me/pat')({
    component: UserPATPage,
})

interface UserToken {
    uid: string
    type: string
    properties?: {
        name?: string
        created_with?: {
            user_agent?: string
            remote_addr?: string
        }
    }
    created_at: string
    last_used_at?: string | null
    expires_at: string
}

function UserPATPage() {
    const { org } = Route.useParams()
    const queryClient = useQueryClient()
    const { t } = useTranslation('users')
    const userUid = 'me'

    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [tokenName, setTokenName] = useState('')
    const [expiresInDays, setExpiresInDays] = useState(365)
    const [createdToken, setCreatedToken] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)
    const [deleteTokenUid, setDeleteTokenUid] = useState<string | null>(null)

    const breadcrumbs = useMemo(() => [
        { title: t('breadcrumb.home'), path: `/${org}` },
        { title: t('breadcrumb.user'), path: `/${org}/user/me/profile` },
        { title: t('breadcrumb.pat') },
    ], [org, t])
    useBreadcrumbs(breadcrumbs)

    const { data: patData, isLoading } = useQuery({
        queryKey: ['userTokens', org, userUid, 'pat'],
        queryFn: () => tokensApi.getUserTokens(org, userUid, 'pat'),
        enabled: !!userUid,
    })

    const createMutation = useMutation({
        mutationFn: () => tokensApi.createToken(org, userUid, tokenName, 'pat', expiresInDays),
        onSuccess: (data) => {
            setCreatedToken(data.token)
            setTokenName('')
            queryClient.invalidateQueries({ queryKey: ['userTokens', org, userUid, 'pat'] })
        },
    })

    const deleteMutation = useMutation({
        mutationFn: (tokenUid: string) => tokensApi.deletePAT(org, userUid, tokenUid),
        onSuccess: () => {
            setDeleteTokenUid(null)
            queryClient.invalidateQueries({ queryKey: ['userTokens', org, userUid, 'pat'] })
        },
    })

    const handleCopy = useCallback((text: string) => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }, [])

    const handleCloseCreate = useCallback(() => {
        setIsCreateOpen(false)
        setCreatedToken(null)
        setTokenName('')
        setCopied(false)
    }, [])

    const formatDate = (dateString: string | null) => {
        if (!dateString) return t('pat.never')
        return new Intl.DateTimeFormat('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(new Date(dateString))
    }

    const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date()

    const tokens = (patData?.tokens || []) as UserToken[]

    return (
        <div className="space-y-6" data-testid="user-pat-page">
            <div>
                <PageHeader
                    icon={SECTION_ICONS.tokens}
                    title={t('pat.title')}
                    description={t('pat.subtitle')}
                    actions={
                    <>
                    <div className="flex items-center gap-2">
                        <a
                            href="/api/docs"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                            data-testid="api-docs-link"
                        >
                            <AppIcon path={mdiOpenInNew} size="sm" />
                            {t('pat.api_docs')}
                        </a>
                        <Dialog open={isCreateOpen} onOpenChange={(open) => {
                            if (!open) handleCloseCreate()
                            else setIsCreateOpen(true)
                        }}>
                            <DialogTrigger asChild>
                                <Button data-testid="create-token-btn">
                                    <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                                    {t('pat.new_token')}
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>
                                        {createdToken ? t('pat.token_created_title') : t('pat.create_token_title')}
                                    </DialogTitle>
                                    <DialogDescription>
                                        {createdToken
                                            ? t('pat.token_created_description')
                                            : t('pat.create_token_description')}
                                    </DialogDescription>
                                </DialogHeader>

                                {createdToken ? (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2">
                                            <code
                                                className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono break-all"
                                                data-testid="created-token-value"
                                            >
                                                {createdToken}
                                            </code>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleCopy(createdToken)}
                                                data-testid="token-copy-btn"
                                            >
                                                {copied ? <AppIcon path={mdiCheck} size="sm" /> : <AppIcon path={mdiContentCopy} size="sm" />}
                                            </Button>
                                        </div>
                                        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-sm text-destructive">
                                            {t('pat.token_created_warning')}
                                        </div>
                                        <DialogFooter>
                                            <Button onClick={handleCloseCreate}>{t('pat.done')}</Button>
                                        </DialogFooter>
                                    </div>
                                ) : (
                                    <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate() }} className="space-y-4">
                                        <div>
                                            <label htmlFor="tokenName" className="block text-sm font-medium text-foreground mb-1">
                                                {t('pat.token_name_label')}
                                            </label>
                                            <input
                                                id="tokenName"
                                                type="text"
                                                value={tokenName}
                                                onChange={(e) => setTokenName(e.target.value)}
                                                placeholder={t('pat.token_name_placeholder')}
                                                required
                                                className="block w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                                data-testid="token-name-input"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="expiresIn" className="block text-sm font-medium text-foreground mb-1">
                                                {t('pat.expiration_label')}
                                            </label>
                                            <select
                                                id="expiresIn"
                                                value={expiresInDays}
                                                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                                                className="block w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                                data-testid="pat-expires-select"
                                            >
                                                <option value={30}>{t('pat.days_30')}</option>
                                                <option value={90}>{t('pat.days_90')}</option>
                                                <option value={180}>{t('pat.days_180')}</option>
                                                <option value={365}>{t('pat.year_1')}</option>
                                                <option value={1095}>{t('pat.years_3')}</option>
                                                <option value={1825}>{t('pat.years_5')}</option>
                                            </select>
                                        </div>
                                        <DialogFooter>
                                            <Button type="button" variant="outline" onClick={handleCloseCreate}>{t('common:actions.cancel')}</Button>
                                            <Button
                                                type="submit"
                                                disabled={createMutation.isPending || !tokenName.trim()}
                                                data-testid="create-pat-submit"
                                            >
                                                {createMutation.isPending ? t('pat.creating') : t('pat.create_button')}
                                            </Button>
                                        </DialogFooter>
                                    </form>
                                )}
                            </DialogContent>
                        </Dialog>
                    </div>
                    </>
                    }
                />
            </div>

            <div>
                {isLoading && (
                    <div className="flex items-center justify-center py-8">
                        <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                        <span className="text-muted-foreground">{t('pat.loading')}</span>
                    </div>
                )}

                {!isLoading && tokens.length === 0 && (
                    <EmptyState
                        variant="inline"
                        icon={mdiKeyVariant}
                        title={t('pat.no_tokens_title')}
                        description={t('pat.no_tokens_description')}
                    />
                )}

                {tokens.length > 0 && (
                    <div className="grid gap-3">
                        {tokens.map((token) => {
                            const expired = isExpired(token.expires_at)
                            return (
                                <Card key={token.uid} className="hover:shadow-md transition-shadow">
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <AppIcon path={mdiKey} size="md" className="text-primary flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h3 className="text-sm font-medium text-foreground truncate">
                                                            {token.properties?.name || t('pat.unnamed_token')}
                                                        </h3>
                                                        {expired && <Badge variant="destructive">{t('pat.expired')}</Badge>}
                                                    </div>
                                                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <AppIcon path={mdiCalendar} size="xs" />
                                                            {t('pat.created', { date: formatDate(token.created_at) })}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <AppIcon path={mdiClockOutline} size="xs" />
                                                            {t('pat.last_used', { date: formatDate(token.last_used_at || null) })}
                                                        </span>
                                                        <span>
                                                            {t('pat.expires', { date: formatDate(token.expires_at) })}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setDeleteTokenUid(token.uid)}
                                                className="text-destructive hover:text-destructive"
                                                data-testid={`delete-token-btn-${token.uid}`}
                                            >
                                                <AppIcon path={mdiTrashCanOutline} size="sm" />
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            )
                        })}
                    </div>
                )}
            </div>

            <AlertDialog open={!!deleteTokenUid} onOpenChange={(open) => { if (!open) setDeleteTokenUid(null) }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('pat.delete_token_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('pat.delete_token_description')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteMutation.isPending}>{t('common:actions.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deleteTokenUid && deleteMutation.mutate(deleteTokenUid)}
                            disabled={deleteMutation.isPending}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteMutation.isPending ? t('pat.deleting') : t('common:actions.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
